import React from "react";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getEllyannaAccessGate,
  REGISTRATION_GATE_MESSAGE,
  SALON_BILLING_LOCK_MESSAGE,
} from "../utils/ellyannaAccessGate";
import {
  canAccessMarketplaceAccountShell,
  canAccessMarketplaceOperatorShell,
} from "../utils/marketplaceOperatorAccess";
import {
  getMarketplaceAccessNeededPath,
  getMarketplaceSuperAdminLandingPath,
} from "../utils/marketplaceShellRouting";
import { isSalonOwnerOrManager } from "../utils/salonRoles";
import SBINModal from "./modals/SBINModal";
import { createSalon, switchSalon } from "../api/superadmin";
import useMyPermissions from "../hooks/useMyPermissions";

const SUPERADMIN_SELECTED_BUSINESS_KEY = "sbin_superadmin_selected_business_id";
const ACCOUNTING_ROUTE_PREFIXES = [
  "/dashboard",
  "/profile",
  "/business-setup",
  "/my-profile",
  "/documents",
  "/requests",
  "/monthly-review",
  "/monthly",
  "/checklist",
  "/cra-access",
  "/data",
  "/source-data",
  "/gst-hst",
  "/payroll",
  "/bookkeeping",
  "/income-taxes",
  "/corporate-tax",
  "/personal-tax",
  "/financial-statements",
  "/workboard",
  "/client-summary",
  "/review",
  "/accountants",
];

function isAccountingPortalPath(path = "") {
  const normalized = String(path || "");
  return (
    normalized.startsWith("/salonbusinesscpa") ||
    ACCOUNTING_ROUTE_PREFIXES.some(
      (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
    )
  );
}

function resolveBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch (err) {
    return "America/New_York";
  }
}

function SetupSalonSelectionRequiredModal({ selectedBusinessId, onCreateSalonAndStartSetup }) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleCancel = () => {
    navigate(-1);
  };

  const normalizedBusinessId = Number(selectedBusinessId);
  const hasSelectedBusiness =
    Number.isFinite(normalizedBusinessId) && normalizedBusinessId > 0;

  const handleCreate = async () => {
    if (!hasSelectedBusiness || !onCreateSalonAndStartSetup) return;
    setIsSubmitting(true);
    setError("");
    try {
      await onCreateSalonAndStartSetup(normalizedBusinessId);
    } catch (err) {
      setError(err?.message || "Unable to create salon and start setup.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-[40vh]" />
      <SBINModal
        isOpen={true}
        onClose={handleCancel}
        title={
          hasSelectedBusiness
            ? "No salon found for the selected business"
            : "Select a salon first"
        }
        size="sm"
        fitContent={true}
        footer={
          <div className="flex justify-end gap-2">
            {hasSelectedBusiness ? (
              <button
                type="button"
                className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                onClick={handleCreate}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating..." : "Create Salon & Start Setup"}
              </button>
            ) : (
              <button
                type="button"
                className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600"
                onClick={() => navigate("/superadmin")}
              >
                Go to Super Admin Console
              </button>
            )}
            {hasSelectedBusiness ? (
              <button
                type="button"
                className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => navigate("/superadmin")}
                disabled={isSubmitting}
              >
                Go to Super Admin Console
              </button>
            ) : null}
            <button
              type="button"
              className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        }
      >
        {hasSelectedBusiness ? (
          <>
            <p className="text-sm text-slate-700">
              You&apos;re in Super Admin mode and no salon is selected. Create a salon under
              the selected business to start setup.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-700">Please select a salon first.</p>
            <p className="mt-2 text-sm text-slate-700">
              To use Salon Setup, please switch into a salon from the Super Admin Console.
            </p>
          </>
        )}
        {error ? (
          <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        ) : null}
      </SBINModal>
    </>
  );
}

function SalonBillingLocked({ message = SALON_BILLING_LOCK_MESSAGE }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const handleBackToLogin = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-[60vh] bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-xl">
        <h2 className="text-xl font-semibold text-slate-900">Salon access paused</h2>
        <p className="mt-3 text-sm leading-6 text-slate-600">{message}</p>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Staff do not need to enter payment details. Please contact the salon owner
          or admin to restore access.
        </p>
        <button
          type="button"
          onClick={handleBackToLogin}
          className="mt-5 inline-flex rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Back to Login
        </button>
      </div>
    </div>
  );
}

export default function ProtectedRoute({
  children,
  allowedRoles,
  allowedPermissions,
  requiredPermissions,
  requireSuperAdmin = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, role, user, login, logout, loading } = useAuth();
  const normalizedAllowedPermissions =
    allowedPermissions && allowedPermissions.length
      ? allowedPermissions.map((key) => String(key || "").trim()).filter(Boolean)
      : null;
  const normalizedRequiredPermissions =
    requiredPermissions && requiredPermissions.length
      ? requiredPermissions.map((key) => String(key || "").trim()).filter(Boolean)
      : null;
  const {
    permissions: effectivePermissions,
    loading: loadingPermissions,
    errorStatus: permissionsErrorStatus,
  } = useMyPermissions({
    enabled: Boolean(
      normalizedAllowedPermissions?.length ||
        normalizedRequiredPermissions?.length
    ),
  });
  const salonIdFromStorage =
    typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id")) || null
      : null;
  const salonId = user?.salon_id ?? salonIdFromStorage ?? null;
  const selectedBusinessId =
    typeof window !== "undefined"
      ? Number(window.localStorage.getItem(SUPERADMIN_SELECTED_BUSINESS_KEY)) || null
      : null;
  const path = location.pathname || "";
  const isSetupRoute = path.startsWith("/setup");
  const isSuperadminRoute = path.startsWith("/superadmin");
  const isSelectSalonRoute = path.startsWith("/select-salon");
  const isBillingRoute = path.startsWith("/manager/manage/billing");
  const isAccountingRoute = isAccountingPortalPath(path);
  const isMarketplaceRoute = path.startsWith("/account/");
  const isMarketplaceOperatorRoute = path.startsWith("/account/operator");
  const isMarketplaceAccessRoute =
    path === "/access-needed" ||
    path.startsWith("/access-needed/") ||
    path === "/account/ellyanna-access" ||
    path.startsWith("/account/ellyanna-access/") ||
    path === "/join-salon" ||
    path.startsWith("/join-salon/");
  const isMarketplaceSharedAccountRoute =
    !isMarketplaceOperatorRoute &&
    (path === "/account/profile" ||
      path.startsWith("/account/profile/") ||
      path === "/account/bookings" ||
      path.startsWith("/account/bookings/") ||
      path === "/account/rentals" ||
      path.startsWith("/account/rentals/") ||
      path === "/account/payments" ||
      path.startsWith("/account/payments/"));
  const allowsNoSalon =
    isSuperadminRoute ||
    isSelectSalonRoute ||
    path === "/change-password" ||
    isAccountingRoute ||
    isMarketplaceSharedAccountRoute ||
    isMarketplaceAccessRoute;
  const billingNext = `${location.pathname}${location.search || ""}`;
  const accessGate = isMarketplaceRoute || isAccountingRoute
    ? null
    : getEllyannaAccessGate(role, user, {
        nextAfterBilling: billingNext,
      });

  const normalizedAllowed = allowedRoles
    ? allowedRoles.map((r) => (r || "").toLowerCase())
    : null;
  const roleKey = (role || "").toLowerCase();
  const hasGlobalAdminAccess =
    Boolean(user?.is_super_admin) ||
    roleKey === "admin" ||
    roleKey === "super_admin" ||
    roleKey === "superadmin";

  useEffect(() => {
    if (permissionsErrorStatus !== 401) return undefined;
    let cancelled = false;
    Promise.resolve(logout?.()).finally(() => {
      if (cancelled) return;
      navigate("/login", {
        replace: true,
        state: {
          sessionExpired: true,
          next: `${location.pathname}${location.search || ""}`,
        },
      });
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, logout, navigate, permissionsErrorStatus]);

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-slate-500">
        Loading...
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (permissionsErrorStatus === 401) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-slate-500">
        Signing you back in...
      </div>
    );
  }

  if (user?.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  if (accessGate?.stage === "billing" && !isBillingRoute) {
    return (
      <Navigate
        to={accessGate.destination}
        replace
      />
    );
  }

  if (accessGate?.stage === "setup" && !isSetupRoute && !isBillingRoute) {
    return (
      <Navigate
        to={accessGate.destination || "/setup"}
        replace
        state={{
          next: billingNext,
          onboardingGateMessage: REGISTRATION_GATE_MESSAGE,
          onboardingGateNonce: Date.now(),
        }}
      />
    );
  }

  if (accessGate?.stage === "salon_billing_locked") {
    return <SalonBillingLocked message={accessGate.message} />;
  }

  if (requireSuperAdmin && !hasGlobalAdminAccess) {
    return (
      <div
        style={{
          maxWidth: "600px",
          margin: "40px auto",
          padding: "24px 20px",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            fontSize: "20px",
            fontWeight: 700,
            marginBottom: "8px",
            color: "#0f172a",
          }}
        >
          You don&apos;t have access to this area
        </h2>
        <p style={{ marginBottom: "16px", color: "#64748b" }}>
          This page is restricted to platform super admins.
        </p>
        <a
          href="/"
          style={{
            display: "inline-block",
            padding: "8px 16px",
            borderRadius: "999px",
            backgroundColor: "#0f766e",
            color: "white",
            textDecoration: "none",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          Go back to homepage
        </a>
      </div>
    );
  }

  const createSalonAndStartSetup = async (businessId) => {
    const createdSalon = await createSalon({
      business_id: businessId,
      name: "New Salon",
      timezone: resolveBrowserTimezone(),
    });
    const salonIdValue = Number(createdSalon?.id);
    if (!Number.isFinite(salonIdValue) || salonIdValue <= 0) {
      throw new Error("Unable to create salon for this business.");
    }

    const switched = await switchSalon(salonIdValue);
    if (!switched?.access_token) {
      throw new Error("Unable to switch to the newly created salon.");
    }
    login(switched.access_token, {
      ...switched,
      superadmin_mode: switched.superadmin_mode,
    });
    navigate("/setup?step=1", { replace: true });
  };

  if (!salonId && !allowsNoSalon) {
    if (hasGlobalAdminAccess) {
      if (isSetupRoute) {
        return (
          <SetupSalonSelectionRequiredModal
            selectedBusinessId={selectedBusinessId}
            onCreateSalonAndStartSetup={createSalonAndStartSetup}
          />
        );
      }
      if (isMarketplaceOperatorRoute) {
        return <Navigate to={getMarketplaceSuperAdminLandingPath()} replace />;
      }
      return <Navigate to="/superadmin" replace />;
    }
    return (
      <Navigate
        to="/select-salon"
        replace
        state={{ next: `${location.pathname}${location.search || ""}` }}
      />
    );
  }

  const marketplaceOperatorShellAllowed = canAccessMarketplaceOperatorShell({
    role: roleKey,
    user,
  });
  const routeUsesPermissions = Boolean(
    normalizedAllowedPermissions?.length ||
      normalizedRequiredPermissions?.length
  );
  const allowedByPermission = normalizedRequiredPermissions?.length
    ? normalizedRequiredPermissions.every((key) => Boolean(effectivePermissions?.[key]))
    : Boolean(
        normalizedAllowedPermissions?.some((key) => effectivePermissions?.[key])
      );
  const canAccessSetup =
    hasGlobalAdminAccess ||
    isSalonOwnerOrManager(roleKey) ||
    allowedByPermission;
  if (
    isSetupRoute &&
    !canAccessSetup &&
    routeUsesPermissions &&
    (loadingPermissions || effectivePermissions === null)
  ) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-slate-500">
        Loading...
      </div>
    );
  }
  if (isSetupRoute && !canAccessSetup) {
    return <Navigate to="/manager/schedule" replace />;
  }

  if (
    isMarketplaceOperatorRoute &&
    !marketplaceOperatorShellAllowed &&
    !hasGlobalAdminAccess
  ) {
    return (
      <Navigate
        to={
          canAccessMarketplaceAccountShell(user)
            ? "/account/profile"
            : getMarketplaceAccessNeededPath({ role: roleKey })
        }
        replace
      />
    );
  }

  if (
    isMarketplaceSharedAccountRoute &&
    !canAccessMarketplaceAccountShell(user) &&
    !hasGlobalAdminAccess
  ) {
    return <Navigate to={getMarketplaceAccessNeededPath({ role: roleKey })} replace />;
  }

  const allowedByRole =
    !normalizedAllowed ||
    normalizedAllowed.includes(roleKey) ||
    (isMarketplaceOperatorRoute && marketplaceOperatorShellAllowed) ||
    hasGlobalAdminAccess;
  const allowedByPrivilegedFallback =
    hasGlobalAdminAccess ||
    roleKey === "admin" ||
    roleKey === "host" ||
    (isSetupRoute && roleKey === "independent_professional") ||
    (isMarketplaceOperatorRoute && marketplaceOperatorShellAllowed);
  const routeAllowed = routeUsesPermissions
    ? allowedByPermission || allowedByPrivilegedFallback
    : allowedByRole;

  if (
    routeUsesPermissions &&
    !allowedByPermission &&
    (loadingPermissions || effectivePermissions === null)
  ) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-slate-500">
        Loading...
      </div>
    );
  }

  if (!routeAllowed) {
    return (
      <div
        style={{
          maxWidth: "600px",
          margin: "40px auto",
          padding: "24px 20px",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            fontSize: "20px",
            fontWeight: 700,
            marginBottom: "8px",
            color: "#0f172a",
          }}
        >
          You don&apos;t have access to this area
        </h2>
        <p style={{ marginBottom: "16px", color: "#64748b" }}>
          Your account is logged in, but your role does not have permission to view this
          page. If you believe this is a mistake, please contact an administrator.
        </p>
        <a
          href="/"
          style={{
            display: "inline-block",
            padding: "8px 16px",
            borderRadius: "999px",
            backgroundColor: "#0f766e",
            color: "white",
            textDecoration: "none",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          Go back to homepage
        </a>
      </div>
    );
  }

  return children;
}

